'use client';

import Image from 'next/image';
import { cn } from '@sharpr/ui';
import type { MatchupBatter } from '@/hooks/useMatchupData';
import { getPlayerImageUrl } from '@/utils/playerImages';
import { lastName } from '@/utils/playerName';
import { TeamLogo } from '../../TeamLogo';

function LineupStatusBadge({ status }: { status: 'Projected' | 'Confirmed' }) {
  const projected = status === 'Projected';
  return (
    <span
      className={cn(
        'text-[8px] font-semibold uppercase tracking-wide px-1 py-px rounded',
        projected
          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      )}
    >
      {status}
    </span>
  );
}

interface TeamColumnProps {
  teamAbbr: string;
  batters: MatchupBatter[];
  lineupStatus?: 'Projected' | 'Confirmed';
  density: 'compact' | 'cozy';
  onBatterClick?: (batter: MatchupBatter, teamAbbr: string) => void;
}

function TeamColumn({ teamAbbr, batters, lineupStatus, density, onBatterClick }: TeamColumnProps) {
  const avatarSize = density === 'compact' ? 20 : 24;
  const numbered = !!lineupStatus;

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 mb-1.5">
        <TeamLogo team={teamAbbr} sport="mlb" className="w-4 h-4" />
        <span className="text-[10px] font-semibold text-gray-200">{teamAbbr}</span>
        <span className="text-[9px] text-gray-400">· {batters.length}</span>
        {lineupStatus && <LineupStatusBadge status={lineupStatus} />}
      </div>
      {batters.length === 0 ? (
        <span className="text-[10px] text-gray-400">—</span>
      ) : (
        <ul className="space-y-0.5">
          {batters.map((batter, i) => {
            const url = batter.playerId
              ? getPlayerImageUrl(batter.name, 'mlb', undefined, batter.playerId) ?? undefined
              : undefined;
            const bats = batter.bats === 'L' || batter.bats === 'R' || batter.bats === 'S' ? batter.bats : undefined;
            const slot = i + 1;
            // The whole row is the click affordance when a handler is wired
            // and the batter has a playerId we can resolve. Falls back to a
            // bare `<div>` (no click chrome) when not actionable, so the
            // directory still renders for batters missing an id.
            const actionable = !!onBatterClick && !!batter.playerId;
            const rowInner = (
              <>
                <span
                  className={cn(
                    'w-3.5 text-[9px] font-mono flex-shrink-0 tabular-nums text-right',
                    numbered ? 'text-gray-300' : 'text-gray-400',
                  )}
                >
                  {numbered ? slot : '·'}
                </span>
                <div
                  className="rounded-full overflow-hidden bg-muted dark:bg-bg-tertiary border border-white/10 flex-shrink-0"
                  style={{ width: avatarSize, height: avatarSize }}
                >
                  {url ? (
                    <Image src={url} alt={batter.name} width={avatarSize} height={avatarSize} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[8px] font-bold text-gray-400">
                      {batter.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-gray-100 truncate flex-1 min-w-0">
                  <span className="md:hidden">{lastName(batter.name) ?? batter.name}</span>
                  <span className="hidden md:inline">{batter.name}</span>
                </span>
                {bats && (
                  <span className="text-[9px] text-gray-400 font-mono flex-shrink-0">{bats}HB</span>
                )}
              </>
            );
            return (
              <li key={`lineup-${slot}-${batter.playerId || batter.name}`}>
                {actionable ? (
                  <button
                    type="button"
                    onClick={() => onBatterClick(batter, teamAbbr)}
                    className="flex w-full items-center gap-2 px-1 py-0.5 text-left rounded transition-colors hover:bg-blue-500/5 hover:text-blue-300 cursor-pointer"
                  >
                    {rowInner}
                  </button>
                ) : (
                  <div className="flex w-full items-center gap-2 px-1 py-0.5 text-left">
                    {rowInner}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface PlayerDirectoryProps {
  homeAbbr: string;
  awayAbbr: string;
  homeBatters: MatchupBatter[];
  awayBatters: MatchupBatter[];
  homeLineupStatus?: 'Projected' | 'Confirmed';
  awayLineupStatus?: 'Projected' | 'Confirmed';
  density?: 'compact' | 'cozy';
  className?: string;
  /** Clicking a batter row opens their research overlay. Receives the batter
   *  + the team abbrev of the column they sit in (needed to resolve their
   *  own team and the opposing team in the parent). */
  onBatterClick?: (batter: MatchupBatter, teamAbbr: string) => void;
}

export function PlayerDirectory({
  homeAbbr,
  awayAbbr,
  homeBatters,
  awayBatters,
  homeLineupStatus,
  awayLineupStatus,
  density = 'compact',
  className,
  onBatterClick,
}: PlayerDirectoryProps) {
  if (homeBatters.length === 0 && awayBatters.length === 0) return null;
  return (
    <div className={cn('flex gap-3', className)}>
      <TeamColumn
        teamAbbr={awayAbbr}
        batters={awayBatters}
        lineupStatus={awayLineupStatus}
        density={density}
        onBatterClick={onBatterClick}
      />
      <div className="w-px bg-border/20 dark:bg-border-secondary" />
      <TeamColumn
        teamAbbr={homeAbbr}
        batters={homeBatters}
        lineupStatus={homeLineupStatus}
        density={density}
        onBatterClick={onBatterClick}
      />
    </div>
  );
}
